import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { ASSETS } from '@/lib/assets';

export default function Accueil() {
  const { t } = useLang();
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    // Wait for both custom fonts (Cormorant Garamond + Inter) to load before
    // revealing the hero. This prevents text reflow/shift during the initial
    // animation on the very first visit when fonts are not cached.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      let cancelled = false;
      // Fallback in case fonts.ready never resolves (very old browsers).
      const timer = setTimeout(() => {
        if (!cancelled) setFontsReady(true);
      }, 1500);
      document.fonts.ready.then(() => {
        if (!cancelled) {
          clearTimeout(timer);
          setFontsReady(true);
        }
      });
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
    setFontsReady(true);
  }, []);

  return (
    <div
      className="relative min-h-screen lg:min-h-screen w-full overflow-hidden"
      data-testid="home-hero"
      style={{
        backgroundImage: `url(${ASSETS.hero})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Subtle navy gradient overlay so text is readable but image stays vivid */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(15,30,61,0.35) 0%, rgba(15,30,61,0.15) 35%, rgba(15,30,61,0.45) 100%)',
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-24 lg:py-32 text-center">
        <div
          className={fontsReady ? 'animate-fade-up' : ''}
          style={{ opacity: fontsReady ? 1 : 0, transition: 'opacity 0.2s ease-out' }}
        >
          <p className="text-[12px] sm:text-[13px] tracking-[0.55em] uppercase text-[var(--color-gold-light)] mb-6 font-medium drop-shadow-md">
            {t.common.tagline}
          </p>

          <h1
            className="font-display text-white tracking-wide leading-[1.05] drop-shadow-xl"
            style={{
              fontSize: 'clamp(2.5rem, 7vw, 6rem)',
              fontWeight: 500,
            }}
          >
            <span className="block">{t.home.titleLine1}</span>
            <span className="block text-[var(--color-gold-light)]">
              {t.home.titleLine2}
            </span>
          </h1>

          <div className="flex items-center justify-center my-8 gap-4">
            <span className="h-px w-12 sm:w-20 bg-[var(--color-gold)]" />
            <span className="w-2 h-2 rotate-45 bg-[var(--color-gold)]" />
            <span className="h-px w-12 sm:w-20 bg-[var(--color-gold)]" />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/cabinet"
              className="inline-flex items-center justify-center px-8 py-3.5 text-[12px] tracking-[0.3em] uppercase font-medium bg-[var(--color-gold)] text-[var(--color-navy)] hover:bg-[var(--color-gold-light)] transition-colors duration-300 shadow-lg"
              data-testid="cta-discover"
            >
              {t.common.discover}
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3.5 text-[12px] tracking-[0.3em] uppercase font-medium border border-[var(--color-gold)] text-[var(--color-gold-light)] hover:bg-[var(--color-gold)] hover:text-[var(--color-navy)] transition-colors duration-300"
              data-testid="cta-contact"
            >
              {t.common.contactUs}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
